import type { BridgeConfig } from '@web/api/types'
import type { IconName } from '@web/components/ui/icons'
import type { PillOption } from '@web/components/ui/PillMenu'

type ChoiceDetails = Omit<PillOption, 'value'>

export const PERMISSIONS: Record<string, ChoiceDetails> = {
  auto: {
    label: 'Auto',
    icon: 'magic',
    description: 'Automatically approve session actions.',
  },
  plan: {
    label: 'Plan',
    icon: 'edit',
    description: 'Plan work with read-only access.',
  },
  dontAsk: {
    label: "Don't ask",
    icon: 'question',
    description: 'Reject actions that require a permission prompt.',
  },
  acceptEdits: {
    label: 'Allow Edits',
    icon: 'edit',
    description:
      'File edits are approved automatically. Shell commands and other tools still ask.',
  },
  default: {
    label: 'Ask for Approval',
    icon: 'question',
    description:
      'Ask before running commands or making changes that require approval.',
  },
  bypassPermissions: {
    label: 'Allow All Commands',
    icon: 'zap',
    description:
      'Everything runs without asking. Only inside an environment you trust.',
  },
}

export const EFFORTS: Record<string, ChoiceDetails> = {
  low: {
    label: 'Low',
    icon: 'clock',
    description: 'Quick answers with minimal thinking. Cheapest and fastest.',
  },
  medium: {
    label: 'Medium',
    icon: 'gauge',
    description:
      'Some deliberation on each step. A good balance for routine work.',
  },
  high: {
    label: 'High',
    icon: 'thinking',
    description: 'Thinks longer on hard problems. The default for coding.',
  },
  xhigh: {
    label: 'Extra high',
    short: 'X-high',
    icon: 'zap',
    description: 'Much more deliberation per step. Slower and more expensive.',
  },
  max: {
    label: 'Max',
    icon: 'agent',
    description:
      'Maximum thinking budget. Slowest and most expensive; for the hardest problems.',
  },
}

export const EXECUTIONS: Record<
  string,
  { icon: IconName; description: string }
> = {
  docker: {
    icon: 'terminal',
    description: 'Runs in a private workspace in this environment.',
  },
  kubernetes: {
    icon: 'agent',
    description: 'Runs in a private workspace in this environment.',
  },
  'claude-managed': {
    icon: 'magic',
    description:
      'Runs on Anthropic with the agent and environment configured for this deployment.',
  },
  'tool-restricted': {
    icon: 'shieldAlert',
    description:
      'No local filesystem or terminal tools. Connected tools may make changes in external services.',
  },
}

export function modelIcon(value: string): IconName {
  if (/haiku|flash|mini|spark/i.test(value)) return 'zap'
  if (/opus|pro|astra/i.test(value)) return 'agent'
  return 'thinking'
}

export function configLabel(id: string, fallback: string): string {
  return (
    (
      {
        model: 'Model',
        effort: 'Effort Level',
        reasoning_effort: 'Effort Level',
        permission_mode: 'Approval Mode',
      } as Record<string, string>
    )[id] ?? fallback
  )
}

export function configChoice(
  id: string,
  value: string,
  label: string,
  description?: string,
  catalog?: BridgeConfig | null,
): PillOption {
  const details =
    id === 'permission_mode'
      ? PERMISSIONS[value]
      : ['effort', 'reasoning_effort'].includes(id)
        ? EFFORTS[value]
        : undefined
  const model =
    id === 'model'
      ? catalog?.models.find(
          (item) => item.id === value || item.aliases?.includes(value),
        )
      : undefined
  return {
    value,
    label,
    icon: id === 'model' ? modelIcon(model?.id ?? value) : 'tool',
    description:
      model?.description || description || `Use ${label} for this session.`,
    ...details,
  }
}
